import { del, get, post } from '@aiptyg/api-client';
import type {
  AssignmentListResponseDTO,
  AssignmentDetailDTO,
  AssignmentDTO,
  ReviewResultDTO,
  DownloadStatsDTO,
  AssignmentSortOrder,
  MessageItemReplaceResultDTO,
  MessageReviewDetailDTO,
  MessageReviewRetryResultDTO,
} from '../types/assignments.types';

export function listAssignments(params: {
  source?: string;
  agentId?: string;
  agentName?: string;
  page: number;
  pageSize: number;
  marketId: string;
  status: string;
  taskId: string;
  userId: string;
  userKeyword: string;
  taskKeyword: string;
  taskType: string;
  platform: string;
  appleIdMatchResult: string;
  order: AssignmentSortOrder;
}): Promise<AssignmentListResponseDTO> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  if (params.source) qs.set('source', params.source);
  if (params.agentId) qs.set('agent_id', params.agentId);
  if (params.agentName?.trim()) qs.set('agent_name', params.agentName.trim());
  qs.set('page_size', String(params.pageSize));
  qs.set('order', params.order);
  if (params.marketId) qs.set('market_id', params.marketId);
  if (params.status) qs.set('status', params.status);
  if (params.taskId) qs.set('task_id', params.taskId);
  if (params.userId) qs.set('user_id', params.userId);
  if (params.userKeyword) qs.set('user_keyword', params.userKeyword);
  if (params.taskKeyword) qs.set('task_keyword', params.taskKeyword);
  if (params.taskType) qs.set('task_type', params.taskType);
  if (params.platform) qs.set('platform', params.platform);
  if (params.appleIdMatchResult) qs.set('apple_id_match_result', params.appleIdMatchResult);
  return get<AssignmentListResponseDTO>(`/v1/admin/assignments?${qs.toString()}`).then(result => ({...result, items:result.items.map(withClaimProvenance)}));
}

export function deleteAssignment(id: number): Promise<void> {
  return del<void>(`/v1/admin/assignments/${id}`);
}

export function getAssignment(id: number): Promise<AssignmentDetailDTO> {
  return get<AssignmentDetailDTO>(`/v1/admin/assignments/${id}`).then(withClaimProvenance);
}

export function reviewAssignment(
  assignmentId: number,
  action: 'approve' | 'reject',
  note: string,
  auditReason?: string,
): Promise<ReviewResultDTO> {
  return post<ReviewResultDTO>(`/v1/admin/assignments/${assignmentId}/review`, {
    action,
    note,
    audit_reason: auditReason ?? note,
  });
}

export function reviewMessageAssignment(
  assignmentId: number,
  action: 'approve' | 'reject',
  note: string,
  auditReason?: string,
): Promise<ReviewResultDTO> {
  const reason = auditReason?.trim() || note.trim();
  return post<ReviewResultDTO>(`/v1/admin/assignments/${assignmentId}/message-review`, {
    action,
    note,
    audit_reason: reason,
  });
}

export function reviewAssignments(
  assignmentIds: number[],
  action: 'approve' | 'reject',
  note: string,
): Promise<{ items: ReviewResultDTO[] }> {
  return post<{ items: ReviewResultDTO[] }>('/v1/admin/assignments/review-batch', {
    assignment_ids: assignmentIds,
    action,
    note,
  });
}

export function getDownloadStats(taskId: number): Promise<DownloadStatsDTO> {
  return get<DownloadStatsDTO>(`/v1/admin/tasks/${taskId}/download-stats`);
}

export function getMessageReviewDetail(assignmentId: number): Promise<MessageReviewDetailDTO> {
  return get<MessageReviewDetailDTO>(`/v1/admin/assignments/${assignmentId}/message-review`);
}

export function retryMessageReview(
  assignmentId: number,
  auditReason: string,
): Promise<MessageReviewRetryResultDTO> {
  return post<MessageReviewRetryResultDTO>(`/v1/admin/assignments/${assignmentId}/message-review/retry`, {
    idempotency_key: `admin-retry-${assignmentId}-${Date.now()}`,
    audit_reason: auditReason,
  });
}

export function replaceMessageItem(
  assignmentId: number,
  newItemId: number,
  auditReason: string,
): Promise<MessageItemReplaceResultDTO> {
  return post<MessageItemReplaceResultDTO>(`/v1/admin/assignments/${assignmentId}/message-item/replace`, {
    new_item_id: newItemId,
    audit_reason: auditReason,
  });
}

export async function fetchMessageEvidenceUrl(evidenceId: number): Promise<string> {
  const result = await get<{ url: string }>(`/v1/admin/message-evidence/${evidenceId}/url`);
  return result.url;
}

function withClaimProvenance<T extends AssignmentDTO>(item:T):T {
  const snapshot=item.claim_snapshot;
  return snapshot ? {...item,source:snapshot.source??item.source,agent_id:snapshot.agent_id??item.agent_id,agent_portal_task_id:snapshot.agent_portal_task_id??item.agent_portal_task_id,agent_portal_revision_id:snapshot.revision_id??item.agent_portal_revision_id} : item;
}
